// iter574 — Extracted from BankStatementsPage.jsx (Parties tab).
// Contains two inner tabs: "Senders / Receivers" and "Party Matches".
//
// Dialogs (match-party + manual-link) stay at the page level since they are
// also opened from the Transactions tab. This component invokes them via
// the `onOpenMatch({sender})` and `onOpenDict({code,label})` callbacks.
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Link2, CheckCircle2, AlertTriangle, Download, Plus } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export function PartiesTab({
  detail,
  analysis,
  partiesFilter,
  setPartiesFilter,
  partyMatchFilter,
  setPartyMatchFilter,
  fmt,
  fmt0,
  onChanged,
  onOpenDict,
  onOpenMatch,
}) {
  return (
    <Tabs defaultValue="senders" data-testid="parties-content-inner">
      <TabsList className="bg-muted/70 p-1 rounded-xl">
        <TabsTrigger value="senders">Senders / Receivers</TabsTrigger>
        <TabsTrigger value="suppliers" data-testid="tab-party-matches">Party Matches</TabsTrigger>
      </TabsList>

      <TabsContent value="senders" className="mt-3 space-y-3">
        {analysis?.parties_summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            <div className="p-3 bg-success/10 rounded-xl border border-success/30">
              <div className="text-[10px] uppercase tracking-wider text-success font-semibold flex items-center gap-1">
                <CheckCircle2 size={11} /> From Suppliers (In)
              </div>
              <div className="text-lg font-bold text-success mt-0.5">{fmt0(analysis.parties_summary.matched_supplier_in)}</div>
              <div className="text-[10px] text-success/70">{analysis.parties_summary.matched_count} matched parties</div>
            </div>
            <div className="p-3 bg-error/10 rounded-xl border border-error/30">
              <div className="text-[10px] uppercase tracking-wider text-error font-semibold flex items-center gap-1">
                <CheckCircle2 size={11} /> To Suppliers (Out)
              </div>
              <div className="text-lg font-bold text-error mt-0.5">{fmt0(analysis.parties_summary.matched_supplier_out)}</div>
              <div className="text-[10px] text-error/70">{analysis.parties_summary.matched_count} matched parties</div>
            </div>
            {(analysis.parties_summary.pos_provider_count || 0) > 0 && (
              <div className="p-3 bg-violet-100 rounded-xl border border-violet-300" data-testid="parties-summary-pos-provider">
                <div className="text-[10px] uppercase tracking-wider text-violet-700 font-semibold flex items-center gap-1">
                  <CheckCircle2 size={11} /> POS Settlements (In)
                </div>
                <div className="text-lg font-bold text-violet-700 mt-0.5">{fmt0(analysis.parties_summary.pos_provider_in)}</div>
                <div className="text-[10px] text-violet-700/70">{analysis.parties_summary.pos_provider_count} provider{analysis.parties_summary.pos_provider_count === 1 ? '' : 's'}</div>
              </div>
            )}
            <div className="p-3 bg-info/10 rounded-xl border border-info/30">
              <div className="text-[10px] uppercase tracking-wider text-info font-semibold flex items-center gap-1">
                <AlertTriangle size={11} /> Unknown In
              </div>
              <div className="text-lg font-bold text-info mt-0.5">{fmt0(analysis.parties_summary.unknown_in)}</div>
              <div className="text-[10px] text-info/70">{analysis.parties_summary.unmatched_count} parties</div>
            </div>
            <div className="p-3 bg-warning/10 rounded-xl border border-warning/30">
              <div className="text-[10px] uppercase tracking-wider text-warning font-semibold flex items-center gap-1">
                <AlertTriangle size={11} /> Unknown Out
              </div>
              <div className="text-lg font-bold text-warning mt-0.5">{fmt0(analysis.parties_summary.unknown_out)}</div>
              <div className="text-[10px] text-warning/70">{analysis.parties_summary.unmatched_count} parties</div>
            </div>
          </div>
        )}

        <div className="flex justify-between items-center gap-2 flex-wrap">
          <div className="flex gap-1.5 flex-wrap">
            {[
              { key: 'all', label: 'All', count: analysis?.senders?.length || 0 },
              { key: 'matched', label: 'Matched suppliers', count: (analysis?.parties_summary?.matched_count || 0) - (analysis?.parties_summary?.pos_provider_count || 0) },
              ...((analysis?.parties_summary?.pos_provider_count || 0) > 0 ? [{ key: 'pos_provider', label: 'POS settlements', count: analysis.parties_summary.pos_provider_count }] : []),
              { key: 'unmatched', label: 'Unmatched', count: analysis?.parties_summary?.unmatched_count || 0 },
            ].map(chip => (
              <button
                key={chip.key}
                type="button"
                onClick={() => setPartiesFilter(chip.key)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                  partiesFilter === chip.key
                    ? 'bg-success text-white border-success'
                    : 'bg-white text-muted-foreground border-border hover:border-success/30'
                }`}
                data-testid={`parties-filter-${chip.key}`}
              >
                {chip.label} <span className="opacity-70">({chip.count})</span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {(() => {
              const unmatchedInbound = (analysis?.senders || []).filter(s =>
                s.match_kind !== 'matched' && s.match_kind !== 'pos_provider' && (s.total_credit || 0) > 0 && (s.name || '').length >= 3
              );
              if (unmatchedInbound.length === 0 || !detail?.id) return null;
              return (
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-xl border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  data-testid="bulk-auto-create-suppliers-btn"
                  onClick={async () => {
                    if (!window.confirm(`Auto-create ${unmatchedInbound.length} suppliers from unmatched inbound rows?\n\nFor each row we'll create a Supplier with the cleaned name + IBAN + bank. Rows whose IBAN or name already exists will be skipped automatically. POS aggregators (Foodics/Geidea/etc.) are excluded.`)) return;
                    try {
                      const r = await api.post(`/bank-statements/${detail.id}/bulk-auto-create-suppliers`, {
                        only_inbound: true,
                        max_create: 200,
                      });
                      const created = r.data?.created || 0;
                      const skipped = r.data?.skipped || 0;
                      const errors = r.data?.errors || 0;
                      if (created > 0) {
                        toast.success(`Created ${created} supplier${created === 1 ? '' : 's'}${skipped ? ` · ${skipped} already existed` : ''}${errors ? ` · ${errors} errors` : ''} — re-matching…`);
                      } else if (skipped > 0) {
                        toast.info(`No new suppliers — all ${skipped} candidate${skipped === 1 ? '' : 's'} already existed.`);
                      } else {
                        toast.warning('No candidates found to create.');
                      }
                      onChanged();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Bulk auto-create failed');
                    }
                  }}
                  title={`Create a Supplier for each of the ${unmatchedInbound.length} unmatched inbound rows`}
                >
                  <Plus size={14} className="mr-1" />Auto-create {unmatchedInbound.length} suppliers
                </Button>
              );
            })()}
            <Button size="sm" variant="outline" className="rounded-xl" onClick={() => {
              if (!analysis?.senders) return;
              const csv = 'Name,Matched Supplier,IBAN,Bank,Count,Received,Sent,Net,Fees,VAT,Period\n' + analysis.senders.map(s => `"${s.name}","${s.supplier_name || ''}","${(s.iban || [])[0] || ''}","${(s.bank || [])[0] || ''}",${s.count},${s.total_credit.toFixed(2)},${s.total_debit.toFixed(2)},${(s.total_credit - s.total_debit).toFixed(2)},${(s.fees || 0).toFixed(2)},${(s.vat || 0).toFixed(2)},"${s.first_date}-${s.last_date}"`).join('\n');
              const blob = new Blob([csv], { type: 'text/csv' }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'senders_receivers.csv'; a.click();
              toast.success('Exported');
            }}><Download size={14} className="mr-1" />Export CSV</Button>
          </div>
        </div>

        <div className="overflow-x-auto border rounded-xl">
          <table className="w-full">
            <thead><tr className="border-b bg-muted/50"><th className="text-left p-2 text-xs font-medium">Name / IBAN / Bank</th><th className="text-left p-2 text-xs font-medium">Match</th><th className="text-center p-2 text-xs font-medium">Times</th><th className="text-right p-2 text-xs font-medium">Received</th><th className="text-right p-2 text-xs font-medium">Sent</th><th className="text-right p-2 text-xs font-medium">Net</th><th className="text-right p-2 text-xs font-medium">Fees/VAT</th></tr></thead>
            <tbody>
              {(() => {
                const filtered = (analysis?.senders || []).filter(s => {
                  if (partiesFilter === 'matched') return s.match_kind === 'matched';
                  if (partiesFilter === 'pos_provider') return s.match_kind === 'pos_provider';
                  if (partiesFilter === 'unmatched') return s.match_kind !== 'matched' && s.match_kind !== 'pos_provider';
                  return true;
                });
                if (filtered.length === 0) {
                  return <tr><td colSpan={7} className="p-8 text-center text-muted-foreground text-sm">No parties in this filter</td></tr>;
                }
                return (
                  <>
                    {filtered.map((s, i) => {
                      const net = (s.total_credit || 0) - (s.total_debit || 0);
                      return (
                        <tr key={i} className={`border-b hover:bg-muted/50 ${s.match_kind === 'matched' ? 'bg-success/10' : s.match_kind === 'pos_provider' ? 'bg-violet-50' : ''}`}>
                          <td className="p-2 text-sm font-medium max-w-xs">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {s.decoded_label && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const m = (s.name || '').match(/[A-Z][A-Z0-9_\-/]{2,}/);
                                    onOpenDict({ code: (m ? m[0] : s.decoded_label).toUpperCase(), label: s.decoded_label });
                                  }}
                                  title={`Auto-decoded from "${s.name || ''}" — click to edit the dictionary`}
                                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-sky-100 text-sky-800 border border-sky-200 hover:bg-sky-200 hover:border-sky-300 transition-colors whitespace-nowrap cursor-pointer"
                                  data-testid={`sender-decoded-badge-${i}`}>
                                  {s.decoded_label}
                                </button>
                              )}
                              <span className="truncate">{s.name}</span>
                            </div>
                            {s.iban?.length > 0 && <div className="text-xs text-muted-foreground font-mono mt-0.5">{s.iban[0]}</div>}
                            {s.bank?.length > 0 && <div className="text-xs text-info">{s.bank[0]}</div>}
                            <div className="text-[10px] text-muted-foreground mt-0.5">{s.first_date}{s.first_date !== s.last_date ? ` → ${s.last_date}` : ''}</div>
                          </td>
                          <td className="p-2">
                            {s.match_kind === 'matched' || s.match_kind === 'pos_provider' ? (
                              <div className="flex items-center gap-1 flex-wrap" data-testid={`party-row-matched-${i}`}>
                                {s.match_kind === 'pos_provider' ? (
                                  <Badge className="bg-violet-200 text-violet-800 text-[10px] gap-0.5 hover:bg-violet-200" data-testid={`party-pos-provider-badge-${i}`} title="Card-settlement aggregator — these inflows are POS sales routed from this provider, not a regular supplier credit.">
                                    <CheckCircle2 size={10} /> {s.pos_provider || s.supplier_name} · POS
                                  </Badge>
                                ) : (
                                  <Badge className="bg-success/20 text-success text-[10px] gap-0.5" data-testid={`party-match-badge-${i}`}>
                                    <CheckCircle2 size={10} /> {s.supplier_name}
                                  </Badge>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-5 text-[9px] rounded-full px-1.5 hover:bg-success/10 hover:border-success/30 opacity-70 hover:opacity-100"
                                  onClick={() => onOpenMatch(s)}
                                  data-testid={`party-add-link-${i}`}
                                  title="Add another link — same IBAN can match a Supplier + Beneficiary + Online Platform simultaneously"
                                >
                                  <Link2 size={9} className="mr-0.5" /> + Add link
                                </Button>
                                {(s.total_credit || 0) > 0 && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-5 text-[9px] rounded-full px-1.5 hover:bg-emerald-50 hover:border-emerald-300 text-emerald-700 opacity-70 hover:opacity-100"
                                    onClick={async () => {
                                      try {
                                        const r = await api.post('/bank-statements/auto-create-supplier', {
                                          raw_name: s.name || '',
                                          iban: (s.iban || [])[0] || '',
                                          bank: (s.bank || [])[0] || '',
                                          account_number: '',
                                        });
                                        toast.success(r.data?.created ? `Created supplier "${r.data.supplier.name}"` : `Linked to "${r.data.supplier.name}"`);
                                        onChanged();
                                      } catch (e) {
                                        toast.error(e.response?.data?.detail || 'Failed');
                                      }
                                    }}
                                    title="Also create / link as Supplier"
                                    data-testid={`party-also-supplier-${i}`}
                                  >+ Supplier</Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-5 text-[9px] rounded-full px-1.5 hover:bg-rose-50 hover:border-rose-300 text-rose-700 opacity-70 hover:opacity-100"
                                  onClick={async () => {
                                    try {
                                      await api.post('/beneficiaries', {
                                        name: s.name || '',
                                        iban: (s.iban || [])[0] || '',
                                        bank: (s.bank || [])[0] || '',
                                        link_type: 'other',
                                        account_number: '',
                                      });
                                      toast.success(`Also saved "${s.name}" as Beneficiary`);
                                      onChanged();
                                    } catch (e) {
                                      toast.error(e.response?.data?.detail || 'Failed');
                                    }
                                  }}
                                  title="Also save as Beneficiary"
                                  data-testid={`party-also-beneficiary-${i}`}
                                >+ Beneficiary</Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 flex-wrap">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-6 text-[10px] rounded-full px-2 hover:bg-success/10 hover:border-success/30"
                                  onClick={() => onOpenMatch(s)}
                                  data-testid={`party-match-btn-${i}`}
                                >
                                  <Link2 size={10} className="mr-1" /> Match to party
                                </Button>
                                {(s.total_credit || 0) > 0 && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-6 text-[10px] rounded-full px-2 hover:bg-emerald-50 hover:border-emerald-300 text-emerald-700"
                                    onClick={async () => {
                                      try {
                                        const r = await api.post('/bank-statements/auto-create-supplier', {
                                          raw_name: s.name || '',
                                          iban: (s.iban || [])[0] || '',
                                          bank: (s.bank || [])[0] || '',
                                          account_number: '',
                                        });
                                        if (r.data?.created) {
                                          toast.success(`Created supplier "${r.data.supplier.name}" — re-matching…`);
                                        } else {
                                          toast.info(`Linked to existing supplier "${r.data.supplier.name}" (${r.data.reason})`);
                                        }
                                        onChanged();
                                      } catch (e) {
                                        toast.error(e.response?.data?.detail || 'Failed to auto-create supplier');
                                      }
                                    }}
                                    data-testid={`party-auto-create-supplier-${i}`}
                                    title="Create a Supplier from this sender (name + IBAN auto-filled) and link future statements automatically"
                                  >
                                    + Supplier
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-6 text-[10px] rounded-full px-2 hover:bg-rose-50 hover:border-rose-300 text-rose-700"
                                  onClick={async () => {
                                    try {
                                      await api.post('/beneficiaries', {
                                        name: s.name || '',
                                        iban: (s.iban || [])[0] || '',
                                        bank: (s.bank || [])[0] || '',
                                        link_type: 'other',
                                        account_number: '',
                                      });
                                      toast.success(`Saved "${s.name}" as Beneficiary — future statements will auto-tag it.`);
                                      onChanged();
                                    } catch (e) {
                                      toast.error(e.response?.data?.detail || 'Failed to save as Beneficiary');
                                    }
                                  }}
                                  data-testid={`party-save-beneficiary-${i}`}
                                  title="Save this party as a Beneficiary so future statements auto-tag it"
                                >
                                  + Beneficiary
                                </Button>
                              </div>
                            )}
                          </td>
                          <td className="p-2 text-center"><Badge variant="secondary" className="text-[10px]">{s.count}x</Badge></td>
                          <td className="p-2 text-sm text-right text-success font-medium">{s.total_credit > 0 ? fmt(s.total_credit) : '-'}</td>
                          <td className="p-2 text-sm text-right text-error font-medium">{s.total_debit > 0 ? fmt(s.total_debit) : '-'}</td>
                          <td className={`p-2 text-sm text-right font-bold ${net >= 0 ? 'text-success' : 'text-error'}`}>{fmt(net)}</td>
                          <td className="p-2 text-xs text-right">{(s.fees || 0) > 0 && <span className="text-warning">Fee:{s.fees.toFixed(1)}</span>}{(s.vat || 0) > 0 && <span className="text-error ml-1">VAT:{s.vat.toFixed(1)}</span>}</td>
                        </tr>
                      );
                    })}
                    <tr className="bg-primary/10 font-bold border-t-2">
                      <td className="p-2 text-sm">TOTAL ({filtered.length} groups)</td>
                      <td></td>
                      <td className="p-2 text-center">{filtered.reduce((s, x) => s + x.count, 0)}</td>
                      <td className="p-2 text-sm text-right text-success">{fmt(filtered.reduce((s, x) => s + x.total_credit, 0))}</td>
                      <td className="p-2 text-sm text-right text-error">{fmt(filtered.reduce((s, x) => s + x.total_debit, 0))}</td>
                      <td className={`p-2 text-sm text-right ${filtered.reduce((s, x) => s + x.total_credit - x.total_debit, 0) >= 0 ? 'text-success' : 'text-error'}`}>{fmt(filtered.reduce((s, x) => s + x.total_credit - x.total_debit, 0))}</td>
                      <td className="p-2 text-xs text-right">Fee:{(filtered.reduce((s, x) => s + (x.fees || 0), 0)).toFixed(1)}</td>
                    </tr>
                  </>
                );
              })()}
            </tbody>
          </table>
        </div>
      </TabsContent>

      <TabsContent value="suppliers" className="mt-3 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <p className="text-xs text-muted-foreground max-w-xl">
            Auto-matches bank transactions to <strong>every party type</strong> — Suppliers, Customers, Employees, Partners, Online platforms, and Beneficiaries — by name or account number / IBAN. Add account numbers on each party's page to improve matching.
          </p>
          <div className="flex items-center gap-1 flex-wrap text-[11px]">
            <span className="text-muted-foreground">Manage:</span>
            <a href="/suppliers" className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100" data-testid="link-suppliers">Suppliers</a>
            <a href="/customers" className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100" data-testid="link-customers">Customers</a>
            <a href="/employees" className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100" data-testid="link-employees">Employees</a>
            <a href="/partners" className="px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100" data-testid="link-partners">Partners</a>
            <a href="/platforms" className="px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-700 border border-cyan-200 hover:bg-cyan-100" data-testid="link-platforms">Online</a>
            <a href="/beneficiaries" className="px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100" data-testid="link-beneficiaries">Beneficiaries</a>
          </div>
        </div>

        {(() => {
          const counts = analysis?.party_counts_by_type || {};
          const total = (analysis?.party_matches || []).length;
          const pills = [
            { k: 'all', label: 'All', count: total, cls: 'bg-slate-100 text-slate-800 border-slate-300' },
            { k: 'supplier', label: 'Suppliers', count: counts.supplier || 0, cls: 'bg-indigo-50 text-indigo-800 border-indigo-200' },
            { k: 'customer', label: 'Customers', count: counts.customer || 0, cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
            { k: 'employee', label: 'Employees', count: counts.employee || 0, cls: 'bg-amber-50 text-amber-800 border-amber-200' },
            { k: 'partner', label: 'Partners', count: counts.partner || 0, cls: 'bg-violet-50 text-violet-800 border-violet-200' },
            { k: 'online', label: 'Online', count: counts.online || 0, cls: 'bg-cyan-50 text-cyan-800 border-cyan-200' },
            { k: 'beneficiary', label: 'Beneficiaries', count: counts.beneficiary || 0, cls: 'bg-rose-50 text-rose-800 border-rose-200' },
          ];
          return (
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="party-filter-pills">
              {pills.map(p => (
                <button
                  key={p.k}
                  type="button"
                  onClick={() => setPartyMatchFilter(p.k)}
                  data-testid={`party-filter-${p.k}`}
                  className={`px-2 py-1 rounded-full text-[11px] border ${partyMatchFilter === p.k ? 'ring-2 ring-offset-1 ring-primary font-semibold ' + p.cls : p.cls + ' opacity-70 hover:opacity-100'}`}
                >
                  {p.label} <span className="opacity-70">({p.count})</span>
                </button>
              ))}
            </div>
          );
        })()}

        {analysis?.party_summary?.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium">Party summary</p>
            {analysis.party_summary
              .filter(s => partyMatchFilter === 'all' || s.entity_type === partyMatchFilter)
              .map((s, i) => (
              <div key={i} className="flex justify-between items-center p-3 border rounded-xl">
                <div className="flex items-center gap-2">
                  <Badge className={
                    s.entity_type === 'supplier' ? 'bg-indigo-100 text-indigo-800 border-indigo-200' :
                    s.entity_type === 'customer' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' :
                    s.entity_type === 'employee' ? 'bg-amber-100 text-amber-800 border-amber-200' :
                    s.entity_type === 'partner' ? 'bg-violet-100 text-violet-800 border-violet-200' :
                    s.entity_type === 'online' ? 'bg-cyan-100 text-cyan-800 border-cyan-200' :
                    'bg-rose-100 text-rose-800 border-rose-200'
                  }>{s.entity_type}</Badge>
                  <span className="font-medium">{s.name}</span>
                  <Badge variant="secondary">{s.match_type === 'account' ? 'By Account #' : s.match_type === 'iban' ? 'By IBAN' : 'By Name'}</Badge>
                </div>
                <div className="flex items-center gap-4">
                  <Badge variant="secondary">{s.count}x</Badge>
                  <span className="font-bold">{fmt(s.total)}</span>
                  <span className="text-xs text-muted-foreground">{s.first_date} → {s.last_date}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="overflow-x-auto border rounded-xl">
          <table className="w-full">
            <thead><tr className="border-b bg-muted/50">
              <th className="text-left p-2 text-xs font-medium">Transaction</th>
              <th className="text-left p-2 text-xs font-medium">Type</th>
              <th className="text-left p-2 text-xs font-medium">Party</th>
              <th className="text-left p-2 text-xs font-medium">Match</th>
              <th className="text-right p-2 text-xs font-medium">Amount</th>
              <th className="text-left p-2 text-xs font-medium">Date</th>
            </tr></thead>
            <tbody>
              {(analysis?.party_matches || [])
                .filter(m => partyMatchFilter === 'all' || m.entity_type === partyMatchFilter)
                .map((m, i) => (
                <tr key={i} className="border-b hover:bg-muted/50" data-testid={`party-match-row-${i}`}>
                  <td className="p-2 text-xs max-w-xs">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {m.decoded_label && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            const tok = (m.transaction || '').match(/[A-Z][A-Z0-9_\-/]{2,}/);
                            onOpenDict({ code: (tok ? tok[0] : m.decoded_label).toUpperCase(), label: m.decoded_label });
                          }}
                          title={`Auto-decoded from "${m.transaction || ''}" — click to edit the dictionary`}
                          className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-sky-100 text-sky-800 border border-sky-200 hover:bg-sky-200 hover:border-sky-300 transition-colors whitespace-nowrap cursor-pointer"
                          data-testid={`partymatch-decoded-badge-${i}`}>
                          {m.decoded_label}
                        </button>
                      )}
                      <span className="truncate">{m.transaction}</span>
                    </div>
                  </td>
                  <td className="p-2">
                    <Badge className={
                      m.entity_type === 'supplier' ? 'bg-indigo-100 text-indigo-800 border-indigo-200 text-[10px]' :
                      m.entity_type === 'customer' ? 'bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]' :
                      m.entity_type === 'employee' ? 'bg-amber-100 text-amber-800 border-amber-200 text-[10px]' :
                      m.entity_type === 'partner' ? 'bg-violet-100 text-violet-800 border-violet-200 text-[10px]' :
                      m.entity_type === 'online' ? 'bg-cyan-100 text-cyan-800 border-cyan-200 text-[10px]' :
                      'bg-rose-100 text-rose-800 border-rose-200 text-[10px]'
                    }>{m.entity_type}</Badge>
                  </td>
                  <td className="p-2"><Badge className="bg-primary/20 text-primary">{m.entity_name || m.supplier}</Badge></td>
                  <td className="p-2"><Badge variant="outline" className="text-xs">{m.match_type}</Badge></td>
                  <td className={`p-2 text-sm text-right font-bold ${m.type === 'credit' ? 'text-success' : 'text-error'}`}>{fmt(m.amount)}</td>
                  <td className="p-2 text-xs">{m.date}</td>
                </tr>
              ))}
              {(!analysis?.party_matches || (analysis.party_matches || []).filter(m => partyMatchFilter === 'all' || m.entity_type === partyMatchFilter).length === 0) && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-muted-foreground text-sm">
                    {partyMatchFilter === 'all' ? (
                      <>
                        No party matches yet for this statement.<br />
                        <span className="text-[11px]">Add account numbers / IBANs to your <a href="/suppliers" className="text-indigo-600 underline">Suppliers</a>, <a href="/customers" className="text-emerald-600 underline">Customers</a>, <a href="/employees" className="text-amber-600 underline">Employees</a>, <a href="/partners" className="text-violet-600 underline">Partners</a>, <a href="/platforms" className="text-cyan-600 underline">Online platforms</a>, or <a href="/beneficiaries" className="text-rose-600 underline">Beneficiaries</a> so this statement auto-tags them.</span>
                      </>
                    ) : (
                      <>No <strong>{partyMatchFilter}</strong> matches in this statement. Try the "All" pill or add account numbers on the <a href={`/${partyMatchFilter === 'online' ? 'platforms' : partyMatchFilter + 's'}`} className="underline">{partyMatchFilter}</a> page.</>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </TabsContent>
    </Tabs>
  );
}
